/**
 * resolve-theme — converte `ThemeMode` em tema efetivo `'light' | 'dark'`.
 *
 * Sub-fase A3.4 do Master Plan A3 (Home Modular).
 *
 * 4 modos:
 *   - `manual_claro`     → sempre `light`
 *   - `manual_escuro`    → sempre `dark`
 *   - `automatico`       → `dark` quando hora atual ∈ [inicio, fim],
 *                          considerando que `fim < inicio` significa janela
 *                          cruza meia-noite (ex.: 19:00–07:00 = noite)
 *   - `sistema`          → segue `prefers-color-scheme` do SO (passado
 *                          como `systemPrefersDark`)
 *
 * Helper puro — sem efeitos colaterais. Toda lógica de tempo é injetada
 * via `nowDate` pra facilitar testes.
 */
import type { ThemeMode } from '@/lib/dashboard/layouts-api';

import { horaEmBrasilia } from '../format-date';
export type EffectiveTheme = 'light' | 'dark';

export interface ResolveThemeInput {
  mode: ThemeMode;
  /** HH:mm — início da janela `dark` no modo `automatico`. */
  autoInicioHhmm?: string | null;
  /** HH:mm — fim da janela `dark` no modo `automatico`. */
  autoFimHhmm?: string | null;
  /** Hora atual injetada (default: `new Date()`). */
  nowDate?: Date;
  /** `prefers-color-scheme: dark` do SO. Usado só no modo `sistema`. */
  systemPrefersDark?: boolean;
}

const DEFAULT_AUTO_INICIO = '19:00';
const DEFAULT_AUTO_FIM = '07:00';

/**
 * Parseia `"HH:mm"` em minutos desde meia-noite. Retorna `null` se inválido.
 */
export function parseHhmm(hhmm: string | null | undefined): number | null {
  if (!hhmm || typeof hhmm !== 'string') return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
}

/**
 * Verifica se `nowMinutes` cai dentro da janela `[inicio, fim]`.
 * Suporta janela que cruza meia-noite (fim < inicio).
 */
export function isWithinAutoWindow(
  nowMinutes: number,
  inicioMinutes: number,
  fimMinutes: number,
): boolean {
  if (inicioMinutes === fimMinutes) {
    // Janela degenerada — sempre dentro (24h).
    return true;
  }
  if (inicioMinutes < fimMinutes) {
    // Janela normal (ex.: 09:00–17:00).
    return nowMinutes >= inicioMinutes && nowMinutes < fimMinutes;
  }
  // Janela cruza meia-noite (ex.: 19:00–07:00).
  return nowMinutes >= inicioMinutes || nowMinutes < fimMinutes;
}

/**
 * Função principal — recebe `ThemeMode` + contexto e retorna tema efetivo.
 *
 * Garantias:
 *   - Determinístico: mesmo input → mesmo output.
 *   - Defaults sensatos: `automatico` sem janelas usa 19:00–07:00 BRT
 *     (horário comum de "noite"); `sistema` sem prefers-color-scheme
 *     assume `light`.
 */
export function resolveEffectiveTheme(input: ResolveThemeInput): EffectiveTheme {
  const {
    mode,
    autoInicioHhmm,
    autoFimHhmm,
    nowDate = new Date(),
    systemPrefersDark = false,
  } = input;

  if (mode === 'manual_claro') return 'light';
  if (mode === 'manual_escuro') return 'dark';
  if (mode === 'sistema') return systemPrefersDark ? 'dark' : 'light';

  // Modo automatico
  const inicioMin =
    parseHhmm(autoInicioHhmm) ?? parseHhmm(DEFAULT_AUTO_INICIO)!;
  const fimMin = parseHhmm(autoFimHhmm) ?? parseHhmm(DEFAULT_AUTO_FIM)!;
  // #484: a janela do modo automático segue o relógio de Brasília, não o do computador.
  const { hora, minuto } = horaEmBrasilia(nowDate);
  const nowMin = hora * 60 + minuto;
  return isWithinAutoWindow(nowMin, inicioMin, fimMin) ? 'dark' : 'light';
}
